import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { ConfigurarGrillaService } from "./configurar-grilla.service";
import { finalize, Subject, Subscription, takeUntil, takeWhile, tap, timer } from "rxjs";
import { AppAlertService } from "@shared/service/app/alert.service";
import { IAlertConfig } from "@shared/models/alert";
import { fuseAnimations } from "@fuse/animations";

interface CheckboxItem {
    identificador?: string;
    ruta?: string;
    columnName?: string;
    columnParam?: string;
    checked?: number; // 0 para no marcado, 1 para marcado
    configuraGrilla?: boolean;
}

export interface ResultConfiguracion {
    identificador: string;
    ruta: string;
    checkboxes: CheckboxItem[];
}

@Component({
    selector: 'app-configurar-grilla',
    templateUrl: './configurar-grilla.component.html',
    standalone: false,
    animations: fuseAnimations
})
export class ConfigurarGrillaComponent implements OnInit, OnDestroy {
    checkboxes: CheckboxItem[] = [];
    mostrarCheckboxes: CheckboxItem[] = [];
    resultConfiguracion: ResultConfiguracion;
    subscription: Subscription;
    showAlert = false;
    _alertConfig: IAlertConfig;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: any,
        public dialogRef: MatDialogRef<ConfigurarGrillaComponent>,
        private configurarGrillaService: ConfigurarGrillaService,
        private alertService: AppAlertService,
    ) {
    }

    ngOnInit(): void {
        this.prepareCheckboxes();
        this.loadCheckboxesState();
        this.subscribeAlert();
    }

    // Alert
    subscribeAlert() {
        this.alertService.emitConfig.subscribe({
            next: (config: IAlertConfig) => {
                this.showAlert = true;
                this._alertConfig = {
                    ...config,
                    appearance: 'border',
                };
                let time = 2;
                timer(100, 1500)
                    .pipe(
                        finalize(() => {
                            this.showAlert = false;
                            this._alertConfig = null;
                        }),
                        takeWhile(() => time > 0),
                        takeUntil(this._unsubscribeAll),
                        tap(() => (time -= 1))
                    )
                    .subscribe();
            },
        });
    }

    prepareCheckboxes() {
        // Asegúrate de que columnNames y columnNamesExtra estén definidos
        const filteredData = Array.isArray(this.data.columnNames) ?
            this.data.columnNames.filter((item: string) => item.trim() !== '') : [];

        const filteredDataExtra = Array.isArray(this.data.columnNamesExtra) ?
            this.data.columnNamesExtra.filter((item: string) => item.trim() !== '') : [];

        // Crear los checkboxes con estado inicial
        this.checkboxes = filteredData.map((item, index) => ({
            columnName: item,
            columnParam: this.data.columnParams[index],
            checked: 1 // Inicialmente no marcado
        }));

        // Agregar las columnas extra si existen
        if (filteredDataExtra.length > 0) {
            this.checkboxes = this.checkboxes.concat(filteredDataExtra.map((item, index) => ({
                columnName: item,
                columnParam: this.data.columnParamsExtra ? this.data.columnParamsExtra[index] : '',
                checked: 0 // Inicialmente no marcado
            })));
        }

        // Agregar el checkbox para configuraGrilla
        this.checkboxes.push({
            configuraGrilla: this.data.configuraGrilla
        });

        this.checkboxes.push({
            identificador: this.data.identificador
        });

        this.checkboxes.push({
            ruta: this.data.ruta
        });

        this.resultConfiguracion = {
            identificador: this.data.identificador,
            ruta: this.data.ruta,
            checkboxes: this.checkboxes
        }
        // Filtrar para excluir el objeto configuraGrilla
        this.mostrarCheckboxes = this.checkboxes.filter(checkbox => !checkbox.configuraGrilla && !checkbox.identificador && !checkbox.ruta);
    }

    loadCheckboxesState() {
        this.subscription = this.configurarGrillaService.checkboxes$.subscribe(resultConfiguracion => {
            if (resultConfiguracion && resultConfiguracion.checkboxes.length > 0) {
                this.checkboxes.forEach(checkbox => {
                    const savedCheckbox = resultConfiguracion.checkboxes.find(saved => saved.columnParam === checkbox.columnParam);
                    if (savedCheckbox) {
                        checkbox.checked = savedCheckbox.checked;
                    }
                });
            }
        });
    }

    toggleCheckbox(checkbox: CheckboxItem) {
        // Cambiar el estado del checkbox
        checkbox.checked = checkbox.checked === 0 ? 1 : 0;
        this.configurarGrillaService.updateCheckboxes(this.resultConfiguracion);
    }

    markAllCheckboxes() {
        // Filtrar checkboxes que tienen las propiedades necesarias
        const validCheckboxes = this.checkboxes.filter(checkbox => checkbox.columnName && checkbox.checked !== undefined);

        const allChecked = validCheckboxes.every(checkbox => {
            return checkbox.checked === 1;
        });

        if (allChecked) {
            this.alertService.warning('Ya fueron seleccionados todos los checkbox.');
        } else {
            this.checkboxes.forEach(checkbox => {
                // Verifica que el checkbox no tenga la propiedad 'configuraGrilla'
                if (!checkbox.configuraGrilla && !checkbox.identificador) {
                    checkbox.checked = 1;
                }
            });
            this.configurarGrillaService.updateCheckboxes(this.resultConfiguracion);
        }
    }

    ngOnDestroy(): void {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    }

    onCloseMenu() {
        this.dialogRef.close(this.checkboxes);
    }
}